import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { useCurrencyFormat } from '@/hooks/useCurrencyFormat';

interface TutorPricingModalProps {
  isOpen: boolean;
  onClose: () => void;
  tutor: {
    name?: string;
    pricingType?: 'hourly' | 'session' | 'level' | 'monthly';
    hourlyRate?: number;
    sessionRate?: number;
    levelRates?: any;
    monthlyRate?: number;
    pricingCurrency?: string;
  } | null;
}

const TutorPricingModal = ({ isOpen, onClose, tutor }: TutorPricingModalProps) => {
  const { formatPrice, selectedCurrency, currencies: contextCurrencies } = useCurrencyFormat();

  // Helper function to convert tutor's price to selected currency
  const formatTutorPrice = (price: number) => {
    if (!price) return formatPrice(0);
    
    const tutorCurrency = tutor?.pricingCurrency || 'USD';
    const tutorCurrencyData = contextCurrencies.find(c => c.code === tutorCurrency);
    
    // Convert tutor's price to USD first
    let priceInUSD = price;
    if (tutorCurrency !== 'USD' && tutorCurrencyData) {
      priceInUSD = price / tutorCurrencyData.rate;
    }
    
    // Use the context's formatPrice to ensure consistent conversion and rounding
    const formatted = formatPrice(priceInUSD);
    
    // For EGP, convert to Arabic numerals
    if (selectedCurrency.code === 'EGP') {
      return formatted.replace(/\d/g, (d) => '٠١٢٣٤٥٦٧٨٩'[parseInt(d)]);
    }
    
    return formatted;
  };

  const getLocalizedText = (text: string) => {
    if (selectedCurrency.code === 'EGP') {
      const translations: { [key: string]: string } = {
        'Pricing Options': 'خيارات الأسعار',
        'Per Hour': 'بالساعة',
        'Per Session': 'بالجلسة',
        'Per Level': 'حسب المستوى',
        'Monthly Subscription': 'اشتراك شهري',
        'hour': 'ساعة',
        'session': 'جلسة',
        'month': 'شهر',
        'No pricing information available': 'لا تتوفر معلومات التسعير'
      };
      return translations[text] || text;
    }
    return text;
  };

  const renderPricingContent = () => {
    if (!tutor) {
      return (
        <div className="text-center py-8 text-muted-foreground">
          {getLocalizedText('No pricing information available')}
        </div>
      );
    }

    const hasAnyPricing = tutor.hourlyRate || tutor.sessionRate || tutor.levelRates || tutor.monthlyRate;
    const isArabic = selectedCurrency.code === 'EGP';
    
    if (!hasAnyPricing) {
      return (
        <div className="text-center py-8 text-muted-foreground">
          {getLocalizedText('No pricing information available')}
        </div>
      );
    }

    // Helper to format price with proper RTL order for Arabic
    const formatPriceWithUnit = (price: number, unit: string) => {
      const formattedPrice = formatTutorPrice(price);
      
      if (isArabic) {
        // For Arabic: Number + Space + Currency + Space + / + Space + Unit
        // Example: ٤٧٠ ج.م / ساعة
        const priceOnly = formattedPrice.replace(selectedCurrency.symbol, '').trim();
        return (
          <div className="flex items-baseline gap-1" dir="rtl">
            <span className="text-2xl font-bold text-foreground">{priceOnly}</span>
            <span className="text-lg font-semibold text-foreground">{selectedCurrency.symbol}</span>
            <span className="text-sm text-muted-foreground">/ {unit}</span>
          </div>
        );
      } else {
        // For English: Currency + Number + / + Unit
        return (
          <div className="flex items-baseline gap-2">
            <p className="text-2xl font-bold text-foreground">
              {formattedPrice}
            </p>
            <span className="text-sm text-muted-foreground">
              / {unit}
            </span>
          </div>
        );
      }
    };

    const pricingOptions = [];
    
    // Hourly Rate - Blue theme
    if (tutor.hourlyRate && tutor.hourlyRate > 0) {
      pricingOptions.push({
        key: 'hourly',
        component: (
          <div className="p-4 bg-blue-50 dark:bg-blue-950/30 rounded-lg border-2 border-blue-200 dark:border-blue-800">
            <div className="flex items-center justify-between">
              <div className="w-full">
                <Badge variant="secondary" className="mb-2 bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300">
                  {getLocalizedText('Per Hour')}
                </Badge>
                {formatPriceWithUnit(tutor.hourlyRate, getLocalizedText('hour'))}
              </div>
            </div>
          </div>
        )
      });
    }

    // Session Rate - Purple theme
    if (tutor.sessionRate && tutor.sessionRate > 0) {
      pricingOptions.push({
        key: 'session',
        component: (
          <div className="p-4 bg-purple-50 dark:bg-purple-950/30 rounded-lg border-2 border-purple-200 dark:border-purple-800">
            <div className="flex items-center justify-between">
              <div className="w-full">
              <Badge variant="secondary" className="mb-2 bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-300">
                  {getLocalizedText('Per Session')}
                </Badge>
                <div className="text-gray-400 dark:text-gray-500 text-[10px] mb-1">(الجلسة أكثر من ساعة ونصف)</div>
                {formatPriceWithUnit(tutor.sessionRate, getLocalizedText('session'))}
              </div>
            </div>
          </div>
        )
      });
    }

    // Level Rates - Green theme - filter out levels with 0 or invalid values
    if (tutor.levelRates && Object.keys(tutor.levelRates).length > 0) {
      const validLevelRates = Object.entries(tutor.levelRates).filter(
        ([_, rate]: [string, any]) => rate && Number(rate) > 0
      );
      
      if (validLevelRates.length > 0) {
        pricingOptions.push({
          key: 'level',
          component: (
            <div className="p-4 bg-green-50 dark:bg-green-950/30 rounded-lg border-2 border-green-200 dark:border-green-800">
              <Badge variant="secondary" className="mb-3 bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300">{getLocalizedText('Per Level')}</Badge>
              <div className="space-y-2">
                {validLevelRates.map(([level, rate]: [string, any]) => {
                  const formattedPrice = formatTutorPrice(rate);
                  const priceDisplay = isArabic ? (
                    <div className="flex items-center gap-1" dir="rtl">
                      <span className="text-lg font-bold text-foreground">
                        {formattedPrice.replace(selectedCurrency.symbol, '').trim()}
                      </span>
                      <span className="text-base font-semibold text-foreground">{selectedCurrency.symbol}</span>
                    </div>
                  ) : (
                    <span className="text-lg font-bold text-foreground">
                      {formattedPrice}
                    </span>
                  );
                  
                  return (
                    <div key={level} className={`flex items-center justify-between p-2 bg-background/50 rounded`}>
                      <span className="font-medium text-foreground capitalize">{level}</span>
                      {priceDisplay}
                    </div>
                  );
                })}
              </div>
            </div>
          )
        });
      }
    }

    // Monthly Rate - Orange theme
    if (tutor.monthlyRate && tutor.monthlyRate > 0) {
      pricingOptions.push({
        key: 'monthly',
        component: (
          <div className="p-4 bg-orange-50 dark:bg-orange-950/30 rounded-lg border-2 border-orange-200 dark:border-orange-800">
            <div className="flex items-center justify-between">
              <div className="w-full">
                <Badge variant="secondary" className="mb-2 bg-orange-100 dark:bg-orange-900 text-orange-700 dark:text-orange-300">
                  {getLocalizedText('Monthly Subscription')}
                </Badge>
                {formatPriceWithUnit(tutor.monthlyRate, getLocalizedText('month'))}
              </div>
            </div>
          </div>
        )
      });
    }

    return (
      <div className="space-y-0">
        {pricingOptions.map((option, index) => (
          <div key={option.key}>
            {option.component}
            {index < pricingOptions.length - 1 && (
              <Separator className="my-4" />
            )}
          </div>
        ))}
      </div>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold">
            {getLocalizedText('Pricing Options')}
          </DialogTitle>
        </DialogHeader>
        {renderPricingContent()}
      </DialogContent>
    </Dialog>
  );
};

export default TutorPricingModal;
